import { FC, ReactNode } from 'react'
import {
  CoinAboutChildBoxStyled,
  CoinAboutChildItemStyled,
  CoinAboutCommunityItemStyled,
  CoinAboutItemStyled,
} from './style'
import BaseDrawer from '@/components/BaseDrawer'
import { Flex } from '@mantine/core'
import BaseImage from '@/components/BaseImage'
import { convertBlockchainAddress, handleRemoveHttp, stopEvent } from '@/utils/tools'
import useCopy from '@/hooks/useCopy'
import { Button, Tag } from '@gate/gui'
import useLangPrefix from '@/hooks/useLangPrefix'
import { useStore } from '@/store/index'
import { observer } from 'mobx-react-lite'
import BaseTooltip from '@/components/BaseTooltip/BaseTooltip'

export interface CoinAboutItemProps {
  title: ReactNode
  list: any[][]
  type?: 'contract' | 'website' | 'browser' | 'community' | 'tags' | 'text'
  isCopy?: boolean
  isSelect?: boolean
  /** 跳转链接 index */
  hrefIndex?: number
  rightIcon?: ReactNode
  isHref?: boolean
}

export const CoinAboutChildItem: FC<
  { child: any[] } & Pick<CoinAboutItemProps, 'isCopy' | 'isSelect' | 'type' | 'hrefIndex'>
> = ({ isSelect, child, isCopy, hrefIndex = 100, type = 'contract' }) => {
  const { runCopy } = useCopy()

  return (
    <CoinAboutChildItemStyled data-select={String(isSelect)}>
      {/* // !原页面打开 */}
      <Flex
        onClick={e => {
          stopEvent(e)
          // child[hrefIndex] && window.open(child[hrefIndex])
          child[hrefIndex] && (location.href = child[hrefIndex])
        }}
        className="coin-about-item-box"
        align="center"
        gap={8}
        w={'max-content'}
      >
        <Flex align="center" gap={4}>
          {type === 'contract' && <BaseImage width={16} height={16} src={child[1]} />}

          {type === 'contract' && (
            <BaseTooltip isToolTipState label={child[0]}>
              <p className="text-1 font-500 size-14 line-120">{convertBlockchainAddress(child[0], 9, 9) ?? '--'}</p>
            </BaseTooltip>
          )}

          {type === 'website' && (
            <svg
              className="iconpark-icon font-16 cursor"
              onClick={e => {
                stopEvent(e)
                runCopy(child[0])
              }}
            >
              <use href="#v31-16px-line-lianjie"></use>
            </svg>
          )}

          {['browser', 'website'].includes(type) && (
            <p className="text-1 font-500 size-14 line-120">{handleRemoveHttp(child[0]) ?? '--'}</p>
          )}
        </Flex>

        {isCopy && (
          <svg
            className="coin-about-item--copy iconpark-icon size-16 cursor"
            onClick={e => {
              stopEvent(e)
              runCopy(child[0])
            }}
          >
            <use href="#v31-16px-line-fuzhi"></use>
          </svg>
        )}
      </Flex>
    </CoinAboutChildItemStyled>
  )
}

export const CoinAboutCommunityItem: FC<
  Pick<CoinAboutItemProps, 'isSelect' | 'type' | 'list' | 'hrefIndex' | 'rightIcon' | 'isHref'>
> = observer(({ isHref = true, isSelect, list, type, hrefIndex = 100, rightIcon }) => {
  const { langPrefix } = useLangPrefix()
  const { isDark } = useStore().globalStore

  if (type === 'tags') {
    return (
      <CoinAboutCommunityItemStyled
        data-select={String(isSelect)}
        gap={isSelect ? 0 : 8}
        {...{ wrap: type === 'tags' ? 'wrap' : 'nowrap' }}
        style={{ paddingLeft: 0, paddingRight: 0 }}
      >
        {isSelect ? (
          <>
            {list?.map(([key, id]) => {
              return (
                <Flex
                  // !新页面打开
                  {...(isHref ? { component: 'a', href: `${langPrefix}/price/view/${id}#table` } : {})}
                  onClick={e => {
                    stopEvent(e)
                    window.open(`${langPrefix}/price/view/${id}#table`)
                    // location.href = `${langPrefix}/price/view/${id}`
                  }}
                  gap={8}
                  key={id}
                  className="coin-about-item-box cursor"
                  align="center"
                  style={{ width: 'calc(100% - 16px)', cursor: isHref ? 'pointer' : 'unset' }}
                >
                  <span className="text-1 font-500 size-14 line-120">{key}</span>
                </Flex>
              )
            })}
          </>
        ) : (
          <>
            {list?.slice(0, 4)?.map(([key, id]) => (
              <a
                key={id}
                {...(isHref ? { component: 'a', href: `${langPrefix}/price/view/${id}#table` } : {})}
                style={{ cursor: isHref ? 'pointer' : 'unset' }}
                // href={`${langPrefix}/price/view/${id}#table`}
                onClick={e => {
                  stopEvent(e)
                  if (isHref) {
                    location.href = `${langPrefix}/price/view/${id}#table`
                  }
                }}
              >
                <Tag bg={'var(--color-cmpt-11)'} label={key} />
              </a>
            ))}
          </>
        )}
        {rightIcon}
      </CoinAboutCommunityItemStyled>
    )
  }

  return (
    <CoinAboutCommunityItemStyled data-select={String(isSelect)} gap={isSelect ? 0 : 8}>
      {isSelect ? (
        <>
          {list?.map(([key, child]) => {
            const name = `${key?.replace(/^./, key[0].toLocaleUpperCase())}`

            let svgHref = name

            if (key === 'website') {
              svgHref = 'v31-16px-line-lianjie'
            }
            return (
              <Flex
                onClick={e => {
                  stopEvent(e)
                  child[hrefIndex] && window.open(child[hrefIndex])
                }}
                gap={8}
                key={name}
                className="coin-about-item-box cursor"
                align="center"
                w={'max-content'}
                style={{ color: isDark && key !== 'website' ? 'var(--color-icon-8)' : 'var(--gui-color-text-1)' }}
              >
                <svg className="iconpark-icon size-20">
                  <use href={`#${svgHref}`}></use>
                </svg>
                <span className="text-1 font-500 size-14 line-120">{name}</span>
              </Flex>
            )
          })}
        </>
      ) : (
        <>
          {list?.slice(0, 3)?.map(([key, child]) => {
            const name = `${key?.replace(/^./, key[0].toLocaleUpperCase())}`

            let svgHref = name

            if (key === 'website') {
              svgHref = 'v31-16px-line-lianjie'
            }
            return (
              <Button
                onClick={e => {
                  stopEvent(e)
                  child[hrefIndex] && window.open(child[hrefIndex])
                }}
                fullWidth
                styles={{
                  label: {
                    color: 'var(--color-text-1)',
                    fontWeight: 500,
                    fontSize: 12,
                  },
                  leftIcon: { color: isDark && key !== 'website' ? 'var(--color-icon-8)' : 'var(--gui-color-text-1)' },
                }}
                key={name}
                type="outlined"
                size="xs"
                label={name}
                leftIcon={
                  <svg className="iconpark-icon size-12">
                    <use href={`#${svgHref}`}></use>
                  </svg>
                }
              />
            )
          })}
        </>
      )}
      {rightIcon}
    </CoinAboutCommunityItemStyled>
  )
})

const CoinAboutItem: FC<CoinAboutItemProps> = ({ title, list, ...props }) => {
  if (['community', 'tags'].includes(props.type ?? '')) {
    return (
      <CoinAboutItemStyled>
        <p className="wrap text-3 font-400 size-14">{title}</p>

        {list.length <= (props.type === 'tags' ? 4 : 3) ? (
          <CoinAboutCommunityItem {...props} list={list} />
        ) : (
          <BaseDrawer
            title={<span className="text-1 font-700 size-16">{title}</span>}
            isButton={false}
            isRight={false}
            target={
              <CoinAboutCommunityItem
                {...props}
                // hrefIndex={100}
                list={list}
                rightIcon={
                  <span>
                    <svg className="iconpark-icon size-16 popover-icon">
                      <use href="#v31-16px-line-xiajiantou"></use>
                    </svg>
                  </span>
                }
              />
            }
          >
            <CoinAboutChildBoxStyled direction={'column'} w={'100%'}>
              <CoinAboutCommunityItem isSelect={true} {...props} list={list} />
            </CoinAboutChildBoxStyled>
          </BaseDrawer>
        )}
      </CoinAboutItemStyled>
    )
  }

  if (['text'].includes(props.type ?? '')) {
    return (
      <CoinAboutItemStyled>
        <p className="wrap text-3 font-400 size-14">{title}</p>
        <p className="text-1 font-500 size-14">{list?.[0]}</p>
      </CoinAboutItemStyled>
    )
  }

  return (
    <CoinAboutItemStyled>
      <p className="wrap text-3 font-400 size-14">{title}</p>

      {list?.length === 1 ? (
        <CoinAboutChildItem {...props} child={list?.[0] ?? []} />
      ) : (
        <BaseDrawer
          title={<span className="text-1 font-700 size-16">{title}</span>}
          // size={'50vh'}
          target={
            <CoinAboutChildItem
              //
              {...props}
              // hrefIndex={100}
              child={list?.[0] ?? []}
            />
          }
        >
          <CoinAboutChildBoxStyled
            //
            direction={'column'}
            w={'100%'}
          >
            {list?.map(item => (
              <CoinAboutChildItem
                //
                isSelect={true}
                key={item?.[0]}
                {...props}
                child={item ?? []}
              />
            ))}
          </CoinAboutChildBoxStyled>
        </BaseDrawer>
      )}
    </CoinAboutItemStyled>
  )
}

export default observer(CoinAboutItem)
